import React, { Component, Fragment } from 'react';
import { hideModal } from '../../../actions/modal.actions';
import { connect } from 'react-redux';
import { createLoadingSelector, getModalData } from '../../../selectors';
import { updateServiceLineBillingDates } from '../../../actions/serviceLine.actions';
import { getServiceLineHistory, getServicesAndEquipment } from '../../../actions/accountDetailsActions/servicesAndEquipment.actions';
import moment from 'moment';
import {toastr} from 'react-redux-toastr';
import DatePicker from 'react-datepicker';

// The three date options exposed in this modal (ORDER_COMPLETION is not applicable here)
const APPLY_ON = {
	START_OF_BILLCYCLE: 'START_OF_BILLCYCLE',
	NEXT_BILLCYCLE: 'NEXT_BILLCYCLE',
	APPLY_ON_DATE: 'APPLY_ON_DATE',
	NO_BILLING: 'NO_BILLING',
};

const INFO = {
	UPDATE_START_DATE: (
		<>
			Selecting Update Billing Start Date will update the date the services were started.
			<strong>&nbsp;All dates matching the selected date will be changed.</strong>
		</>
	),
	UPDATE_STOP_DATE: (
		<>
			Selecting Update Billing Stop Date will update the date the services were stopped.
			<strong>&nbsp;All dates matching the selected date will be changed.</strong>
		</>
	),
	START_OF_BILLCYCLE: (
		<>
			Sets the date to the <strong>first day of the current billing cycle</strong> based on the
			account&apos;s bill cycle day. This is the most recent billing cycle start prior to today.
		</>
	),
	NEXT_BILLCYCLE: (
		<>
			Sets the date to the <strong>first day of the next billing cycle</strong>. This is the
			upcoming billing cycle start following today.
		</>
	),
	APPLY_ON_DATE: (
		<>
			Lets you <strong>pick a specific date</strong> using the date picker. Only dates that satisfy
			the validation rules for the selected date type (start or stop) are available.
		</>
	),
	NO_BILLING: (
		<>
			Sets the end date to match the service start date. This removes the charge and any prorated amount from your next bill.
	<strong> Each service will be stopped as of its own start date.</strong>
		</>
	),
};

class UpdateBillingDateModal extends Component {

	constructor(props) {
		super(props);
		const { currentEndDate } = props.modalProps;
		this.state = {
			updateType: currentEndDate ? 'STOP_DATE' : 'START_DATE',
			applyOnType: APPLY_ON.START_OF_BILLCYCLE,
			currentDate: null,
			applyOnDate: null,
			openInfoKey: null,
			saveError: null,
			serviceLineHistory: null,
		};
	}

	componentDidMount() {
		const { serviceLineId } = this.props.modalProps;
		if (serviceLineId) {
			this.props.getServiceLineHistory(serviceLineId).then(response => {
				this.setState({ serviceLineHistory: response });
			});
		}
	}

	getHistoryStartDates = () => {
		const { serviceLineHistory } = this.state;
		if (!serviceLineHistory) return [];
		const seen = new Set();
		const dates = [];
		const add = (d) => { if (d && !seen.has(d)) { seen.add(d); dates.push(d); } };
		add(serviceLineHistory.startDate);
		(serviceLineHistory.servicelineNums || []).forEach(servicelineNum => add(servicelineNum.startDate));
		(serviceLineHistory.servicelineFeatures || []).forEach(servicelineFeature => add(servicelineFeature.startDate));
		return dates.sort();
	};

	getHistoryEndDates = () => {
		const { serviceLineHistory } = this.state;
		if (!serviceLineHistory) return [];
		const seen = new Set();
		const dates = [];
		const add = (d) => { if (d && !seen.has(d)) { seen.add(d); dates.push(d); } };
		add(serviceLineHistory.endDate);
		(serviceLineHistory.servicelineNums || []).forEach(servicelineNum => add(servicelineNum.endDate));
		(serviceLineHistory.servicelineFeatures || []).forEach(servicelineFeature => add(servicelineFeature.endDate));
		return dates.sort();
	};

	// ─── Date calculation helpers ────────────────────────────────────────────

	getBillCycleDay = () => {
		const { accountInfo } = this.props.modalProps;
		return accountInfo && accountInfo.billCycle ? parseInt(accountInfo.billCycle, 10) : new Date().getDate();
	};

	getStartOfBillCycleDate = () => {
		const billCycleDay = this.getBillCycleDay();
		const now = new Date();
		const currentDay = now.getDate();
		if (billCycleDay <= currentDay) {
			return new Date(now.getFullYear(), now.getMonth(), billCycleDay);
		}
		return new Date(now.getFullYear(), now.getMonth() - 1, billCycleDay);
	};

	getNextBillCycleDate = () => {
		const billCycleDay = this.getBillCycleDay();
		const now = new Date();
		const currentDay = now.getDate();
		if (billCycleDay <= currentDay) {
			return new Date(now.getFullYear(), now.getMonth() + 1, billCycleDay);
		}
		return new Date(now.getFullYear(), now.getMonth(), billCycleDay);
	};

	getCalculatedDate = () => {
		const { applyOnType, applyOnDate } = this.state;
		if (applyOnType === APPLY_ON.START_OF_BILLCYCLE) return this.getStartOfBillCycleDate();
		if (applyOnType === APPLY_ON.NEXT_BILLCYCLE)    return this.getNextBillCycleDate();
		if (applyOnType === APPLY_ON.APPLY_ON_DATE)     return applyOnDate || null;
		if (applyOnType === APPLY_ON.NO_BILLING)        return 'NO_BILLING';
		return null;
	};

	// ─── Feature/num date boundary helpers ───────────────────────────────────

	getEarliestEndDate = () => {
		const { serviceLineHistory, currentDate } = this.state;
		if (!serviceLineHistory || !currentDate) return null;
		let earliest = null;
		const check = (d) => {
			if (d && (!earliest || moment(d).isBefore(moment(earliest), 'day'))) earliest = d;
		};
		(serviceLineHistory.servicelineFeatures || []).filter(servicelineFeature => servicelineFeature.startDate === currentDate).forEach(servicelineFeature => check(servicelineFeature.endDate));
		(serviceLineHistory.servicelineNums || []).filter(servicelineNum => servicelineNum.startDate === currentDate).forEach(servicelineNum => check(servicelineNum.endDate));
		return earliest;
	};

	getLatestStartDate = () => {
		const { serviceLineHistory, currentDate } = this.state;
		if (!serviceLineHistory || !currentDate) return null;
		let latest = null;
		const check = (d) => {
			if (d && (!latest || moment(d).isAfter(moment(latest), 'day'))) latest = d;
		};
		(serviceLineHistory.servicelineFeatures || []).filter(servicelineFeature => servicelineFeature.endDate === currentDate).forEach(servicelineFeature => check(servicelineFeature.startDate));
		(serviceLineHistory.servicelineNums || []).filter(servicelineNum => servicelineNum.endDate === currentDate).forEach(servicelineNum => check(servicelineNum.startDate));
		return latest;
	};

	// ─── Validation ──────────────────────────────────────────────────────────

	getValidationError = () => {
		const { updateType, applyOnType } = this.state;
		const { currentStartDate, currentEndDate } = this.props.modalProps;
		const calculatedDate = this.getCalculatedDate();
		const billCycleDate = this.getStartOfBillCycleDate();

		if (!calculatedDate) return null;
		if (applyOnType === APPLY_ON.NO_BILLING) {
			const { currentDate } = this.state;
			const billCycleStart = moment(billCycleDate).startOf('day');
			if (currentDate && moment(currentDate, 'YYYY-MM-DD').isBefore(billCycleStart, 'day')) {
				return `No Billing is only available for dates within the current billing cycle (starting ${billCycleStart.format('MM/DD/YYYY')}).`;
			}
			return null;
		}

		if (updateType === 'START_DATE') {
			// Cannot be earlier than the current billing cycle day
			if (moment(calculatedDate).isBefore(moment(billCycleDate), 'day')) {
				return `The start date cannot be set before the current billing cycle date (${moment(billCycleDate).format('MM/DD/YYYY')}).`;
			}
			// Cannot go past the stop date
			if (currentEndDate && moment(calculatedDate).isAfter(moment(currentEndDate), 'day')) {
				return `The start date cannot be set past the stop date (${moment(currentEndDate).format('MM/DD/YYYY')}).`;
			}
			// Cannot go past the earliest feature/num end date
			const earliestEnd = this.getEarliestEndDate();
			if (earliestEnd && moment(calculatedDate).isAfter(moment(earliestEnd), 'day')) {
				return `The start date cannot be set after the earliest feature end date (${moment(earliestEnd).format('MM/DD/YYYY')}).`;
			}
			// Cannot go past the next bill cycle date
			const nextBillCycle = this.getNextBillCycleDate();
			if (moment(calculatedDate).isAfter(moment(nextBillCycle), 'day')) {
				return `The start date cannot be set past the next billing cycle date (${moment(nextBillCycle).format('MM/DD/YYYY')}).`;
			}
		}

		if (updateType === 'STOP_DATE') {
			// Cannot be before the start date
			if (currentStartDate && moment(calculatedDate).isBefore(moment(currentStartDate), 'day')) {
				return `The stop date cannot be set before the plan's current start date (${moment(currentStartDate).format('MM/DD/YYYY')}).`;
			}
			// Cannot be before the latest feature/num start date
			const latestStart = this.getLatestStartDate();
			if (latestStart && moment(calculatedDate).isBefore(moment(latestStart), 'day')) {
				return `The stop date cannot be set before the latest feature start date (${moment(latestStart).format('MM/DD/YYYY')}).`;
			}
		}

		return null;
	};

	// ─── Handlers ────────────────────────────────────────────────────────────

	handleUpdateTypeChange = (updateType) => {
		this.setState({ updateType, applyOnType: APPLY_ON.START_OF_BILLCYCLE, currentDate: null, applyOnDate: null, saveError: null, openInfoKey: null });
	};

	handleApplyOnTypeChange = (applyOnType) => {
		this.setState({ applyOnType, saveError: null });
	};

	handleCurrentDateChange = (date) => {
		this.setState({ currentDate: date, saveError: null });
	};

	handleDateChange = (date) => {
		this.setState({ applyOnDate: date, saveError: null });
	};

	toggleInfo = (key) => {
		this.setState(prev => ({ openInfoKey: prev.openInfoKey === key ? null : key }));
	};

	closeModal = () => {
		this.props.hideModal();
	};

	handleSave = () => {
		const validationError = this.getValidationError();
		if (validationError) {
			this.setState({ saveError: validationError });
			return;
		}

		const calculatedDate = this.getCalculatedDate();
		if (!calculatedDate) {
			this.setState({ saveError: 'Please select a date before saving.' });
			return;
		}

		const { accountId, serviceLineId, showHistoricalData, onSuccess } = this.props.modalProps;
		const { updateType, applyOnType, currentDate } = this.state;

		const payload = {
			dateType: updateType,
			currentDate: currentDate ? moment(currentDate).format('YYYY-MM-DD') : null,
			applyOnType: applyOnType,
			applyOnDate: applyOnType === APPLY_ON.NO_BILLING ? null : moment(calculatedDate).format('YYYY-MM-DD'),
		};

		this.props.updateServiceLineBillingDates(accountId, serviceLineId, payload)
			.then(response => {
				if (response && response.data && response.data.success) {
					toastr.success(response.data.message || 'Billing dates updated successfully.', { timeOut: 3000, position: 'top-center' });
					this.props.getServicesAndEquipment(accountId, showHistoricalData);
					if (onSuccess) onSuccess();
					this.closeModal();
				} else {
					const msg = response?.data?.error?.message
						|| 'The save could not be completed. All changes have been rolled back.';
					toastr.error(msg, { timeOut: 0, position: 'top-center' });
					this.setState({ saveError: msg });
				}
			})
			.catch(() => {
				const msg = 'The save could not be completed. All changes have been rolled back.';
				toastr.error(msg, { timeOut: 0, position: 'top-center' });
				this.setState({ saveError: msg });
			});
	};

	// ─── Render helpers ──────────────────────────────────────────────────────

	renderInfoPopover(key) {
		if (this.state.openInfoKey !== key) return null;
		return (
			<div className="alert alert-info py-2 px-3 mb-2 mt-1" style={{ fontSize: '0.85rem' }}>
				{INFO[key]}
			</div>
		);
	}

	renderInfoButton(key) {
		return (
			<button
				type="button"
				className="btn btn-xs btn-desc-toggler ml-1"
				onClick={(e) => { e.preventDefault(); this.toggleInfo(key); }}
				title="More information"
			>
				<i className="fas fa-info-circle" />
			</button>
		);
	}

	render() {
		const { updateType, applyOnType, currentDate, applyOnDate, saveError } = this.state;
		const { planDescription, serviceModelIcon, serviceLineAddress, serviceLineNumber, currentStartDate, currentEndDate } = this.props.modalProps;
		const { saving } = this.props;

		const historyStartDates = this.getHistoryStartDates();
		const historyEndDates   = this.getHistoryEndDates();

		const startOfBillCycleDate = this.getStartOfBillCycleDate();
		const nextBillCycleDate    = this.getNextBillCycleDate();
		const validationError      = this.getValidationError();
		const displayError         = saveError || validationError;

		// DatePicker constraints depend on which date we are updating
		const earliestEnd = this.getEarliestEndDate();
		const latestStart = this.getLatestStartDate();

		let datePickerMin, datePickerMax;
		if (updateType === 'START_DATE') {
			datePickerMin = startOfBillCycleDate;
			const endCandidates = [currentEndDate, earliestEnd, nextBillCycleDate].filter(Boolean).map(d => new Date(d));
			datePickerMax = endCandidates.length > 0
				? new Date(Math.min(...endCandidates))
				: undefined;
		} else {
			const startCandidates = [currentStartDate, latestStart].filter(Boolean).map(d => new Date(d));
			datePickerMin = startCandidates.length > 0
				? new Date(Math.max(...startCandidates))
				: undefined;
			datePickerMax = undefined;
		}

		const saveDisabled = saving
			|| !!validationError
			|| !currentDate
			|| (applyOnType === APPLY_ON.APPLY_ON_DATE && !applyOnDate);

		return (
			<Fragment>
				<div className="modal" style={{ display: 'block' }} tabIndex="-1" role="dialog">
					<div className="modal-dialog modal-dialog-scrollable modal-dialog-centered" role="document">
						<div className="modal-content">

							{/* Header */}
							<div className="modal-header">
								<h5 className="modal-title">Update Billing Dates</h5>
								<button type="button" className="close" onClick={this.closeModal}>
									<span aria-hidden="true">&times;</span>
								</button>
							</div>

							{/* Body */}
							<div className="modal-body form-horizontal">

								{/* ── Error message ── */}
								{displayError && (
									<div className="alert alert-danger mt-0 mb-3">
										{displayError}
									</div>
								)}

								{/* ── Service / plan identity header (mirrors DetailedDescriptionModal) ── */}
								<div className="order-service-order-wrapper">
									<div>
										<h4 className="text-muted"><i className={serviceModelIcon} />&nbsp;{planDescription}</h4>
										{serviceLineAddress && (
											<p className="billing-job-id">
												<i className="fas fa-map-marker-alt fa-fw mr-1 ml-1 text-muted" />
												{serviceLineAddress}
											</p>
										)}
										{serviceLineNumber && (
											<p className="billing-job-id">
												{serviceModelIcon && serviceModelIcon.includes('phone')
													? <i className="fas fa-hashtag fa-fw mr-1 ml-1 text-muted" />
													: <i className={serviceModelIcon + ' fa-fw mr-1 ml-1 text-muted'} />
												}
												{serviceLineNumber}
											</p>
										)}
									</div>
								</div>

								{/* ── Section 1: Which date to update ── */}
								<div className="form-group">
									<label className="font-weight-bold" style={{ fontSize: '0.9rem' }}>Select to update Billing Start Date or the Billing Stop Date.</label>

									{/* Update Start Date */}
									<div className="form-check checkbox-slider checkbox-slider--b-flat mb-1">
										<label>
											<input
												type="radio"
												name="updateType"
												value="START_DATE"
												checked={updateType === 'START_DATE'}
												onChange={() => this.handleUpdateTypeChange('START_DATE')}
												disabled={historyStartDates.length === 0}
											/>
											<span>Update Billing Start Date</span>
											{this.renderInfoButton('UPDATE_START_DATE')}
										</label>
									</div>
									{this.renderInfoPopover('UPDATE_START_DATE')}

									{/* Historical start dates — shown when Update Start Date is active */}
									{updateType === 'START_DATE' && (() => {
										return historyStartDates.length > 0 ? (
											<select
												className="form-control mb-3"
												value={currentDate || ''}
												onChange={e => this.handleCurrentDateChange(e.target.value || null)}
											>
												<option value="">Select a start date to change...</option>
												{historyStartDates.map(d => (
													<option key={d} value={d}>{moment(d).format('MM/DD/YYYY')}</option>
												))}
											</select>
										) : (
											<p className="text-muted text-xs mb-2">Loading start dates...</p>
										);
									})()}

									{/* Update Stop Date */}
									<div className="form-check checkbox-slider checkbox-slider--b-flat mb-1">
										<label>
											<input
												type="radio"
												name="updateType"
												value="STOP_DATE"
												checked={updateType === 'STOP_DATE'}
												onChange={() => this.handleUpdateTypeChange('STOP_DATE')}
												disabled={historyEndDates.length === 0}
											/>
											<span>Update Billing Stop Date</span>
											{this.renderInfoButton('UPDATE_STOP_DATE')}
										</label>
									</div>
									{this.renderInfoPopover('UPDATE_STOP_DATE')}

									{/* Historical end dates — shown when Update Stop Date is active */}
									{updateType === 'STOP_DATE' && (() => {
										return historyEndDates.length > 0 ? (
											<select
												className="form-control mb-3"
												value={currentDate || ''}
												onChange={e => this.handleCurrentDateChange(e.target.value || null)}
											>
												<option value="">Select a stop date to change...</option>
												{historyEndDates.map(d => (
													<option key={d} value={d}>{moment(d).format('MM/DD/YYYY')}</option>
												))}
											</select>
										) : (
											<p className="text-muted text-xs mb-2">Loading stop dates...</p>
										);
									})()} 
								</div>

								<hr />

								{/* ── Section 2: Select new date ── */}
								<div className="form-group">
									<div className="d-flex align-items-center mb-2">
										<label className="font-weight-bold mb-0" style={{ fontSize: '0.9rem' }}>Select New Date</label>
									</div>

									{/* Start of Bill Cycle */}
									<div className="form-check checkbox-slider checkbox-slider--b-flat mb-1">
										<label>
											<input
												type="radio"
												name="applyOnType"
												value={APPLY_ON.START_OF_BILLCYCLE}
												checked={applyOnType === APPLY_ON.START_OF_BILLCYCLE}
												onChange={() => this.handleApplyOnTypeChange(APPLY_ON.START_OF_BILLCYCLE)}
											/>
											<span>Start of Bill Cycle</span>
											{this.renderInfoButton('START_OF_BILLCYCLE')}
										</label>
									</div>
									{this.renderInfoPopover('START_OF_BILLCYCLE')}

									{/* Next Bill Cycle */}
									<div className="form-check checkbox-slider checkbox-slider--b-flat mb-1">
										<label>
											<input
												type="radio"
												name="applyOnType"
												value={APPLY_ON.NEXT_BILLCYCLE}
												checked={applyOnType === APPLY_ON.NEXT_BILLCYCLE}
												onChange={() => this.handleApplyOnTypeChange(APPLY_ON.NEXT_BILLCYCLE)}
											/>
											<span>Next Bill Cycle</span>
											{this.renderInfoButton('NEXT_BILLCYCLE')}
										</label>
									</div>
									{this.renderInfoPopover('NEXT_BILLCYCLE')}

									{/* Select a Date */}
									<div className="form-check checkbox-slider checkbox-slider--b-flat mb-1">
										<label>
											<input
												type="radio"
												name="applyOnType"
												value={APPLY_ON.APPLY_ON_DATE}
												checked={applyOnType === APPLY_ON.APPLY_ON_DATE}
												onChange={() => this.handleApplyOnTypeChange(APPLY_ON.APPLY_ON_DATE)}
											/>
											<span>Select a Date</span>
											{this.renderInfoButton('APPLY_ON_DATE')}
										</label>
									</div>
									{this.renderInfoPopover('APPLY_ON_DATE')}

									{/* No Billing — only available for stop dates */}
									{updateType === 'STOP_DATE' && (
										<>
											<div className="form-check checkbox-slider checkbox-slider--b-flat mb-1">
												<label>
													<input
														type="radio"
														name="applyOnType"
														value={APPLY_ON.NO_BILLING}
														checked={applyOnType === APPLY_ON.NO_BILLING}
														onChange={() => this.handleApplyOnTypeChange(APPLY_ON.NO_BILLING)}
													/>
													<span>Set Stop Date for No Billing</span>
													{this.renderInfoButton('NO_BILLING')}
												</label>
											</div>
											{this.renderInfoPopover('NO_BILLING')}
										</>
									)}

									{/* DatePicker — shown only when "Select a Date" is active */}
									{applyOnType === APPLY_ON.APPLY_ON_DATE && (
										<div className="customDatePickerWidth mt-2">
											<DatePicker
												className="form-control text-left"
												dateFormat="MM/dd/yyyy"
												placeholderText="Select Date"
												autoFocus={false}
												shouldCloseOnSelect={true}
												popperPlacement="auto"
												selected={applyOnDate && moment(applyOnDate).isValid() ? moment(applyOnDate).toDate() : null}
												onChange={this.handleDateChange}
												maxDate={datePickerMax}
												minDate={datePickerMin}
												isClearable={true}
											/>
										</div>
									)}
								</div>
							</div>{/* end modal-body */}

							{/* Footer */}
							<div className="modal-footer">
								<button
									type="button"
									className="btn btn-secondary"
									onClick={this.closeModal}
									disabled={saving}
								>
									Cancel
								</button>
								<button
									type="button"
									className="btn btn-primary"
									onClick={this.handleSave}
									disabled={saveDisabled}
								>
									Save
									{saving && <i className="fas fa-fw fa-spin fa-spinner ml-1" />}
								</button>
							</div>

						</div>
					</div>
				</div>
				<div className="modal-backdrop show" tabIndex="1" />
			</Fragment>
		);
	}
}

const savingSelector = createLoadingSelector(['UPDATE_SERVICELINE_BILLING_DATES']);

const mapStateToProps = (state) => ({
	modalProps: getModalData(state).modalProps,
	saving: savingSelector(state),
});

const mapDispatchToProps = {
	hideModal,
	updateServiceLineBillingDates,
	getServiceLineHistory,
	getServicesAndEquipment,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateBillingDateModal);
